import axios from 'axios';

import { BrandingPreviewDto, PracticeBrandingDto } from '../models/BrandingDTO';

import { isViewAs } from '../viewAs/viewAsSession';

import { healioSpaceAPIURL } from './baseHealioSpaceAPI';
import { installSessionInterceptors } from './sessionInterceptors';
/**
 * Authenticated API client for practice branding
 * Requires user to be logged in with valid idToken
 */
export const brandingApi = axios.create({
  baseURL: `${healioSpaceAPIURL}/api/v1/practice/branding`,
  withCredentials: true,
});

installSessionInterceptors(brandingApi);

/**
 * Public API client for branding by subdomain
 * Does not require authentication
 */
export const publicBrandingApi = axios.create({
  baseURL: `${healioSpaceAPIURL}/api/v1/branding`,
  withCredentials: false,
});

/**
 * HA-267: Authenticated API client for the JWT-scoped branding endpoint.
 * Same base path as publicBrandingApi but with credentials + session
 * interceptors. /me always returns 200 with default branding fallback,
 * so this client never produces the 400-storm the legacy /preview
 * endpoint did for Clients.
 */
export const myBrandingApi = axios.create({
  baseURL: `${healioSpaceAPIURL}/api/v1/branding`,
  withCredentials: true,
});

installSessionInterceptors(myBrandingApi);

// ============================================
// Authenticated Endpoints (for logged-in users)
// ============================================

/**
 * Get the current user's practice branding configuration.
 * This is the primary endpoint for client dashboard branding.
 */
export const getPracticeBranding = async (): Promise<PracticeBrandingDto | null> => {
  try {
    const response = await brandingApi.get<PracticeBrandingDto>('');

    return response.data;
  } catch (error) {
    console.error('Error getting practice branding:', error);

    return null;
  }
};

/**
 * HA-267: JWT-scoped branding fetch. The backend resolves the caller's
 * practice from the token and returns either custom practice branding
 * (when the practice's license tier has PracticeBrandingEnabled) or
 * default Healio branding. ALWAYS returns 200 with a valid payload —
 * Clients no longer see 400s, and orphan / missing-practice users get
 * default branding instead of an error.
 *
 * HA-276: the deprecated `getBrandingPreview` /
 * `getBrandingWithCache` / `getCachedBranding` / `setCachedBranding`
 * wrappers were removed alongside this swap. They had no callers in
 * the production tree and were inviting accidental re-imports during
 * future edits. The legacy `clearCachedBranding` helper remains as a
 * small transitional cleanup that wipes the pre-HA-267 localStorage
 * key on logout — used by BrandingContext.clearBranding so users who
 * still carry stale legacy cache entries get them flushed.
 */
export const getMyBranding = async (): Promise<BrandingPreviewDto | null> => {
  try {
    const response = await myBrandingApi.get<BrandingPreviewDto>('/me');

    return response.data;
  } catch (error) {
    console.error('Error getting my branding:', error);

    return null;
  }
};

// ============================================
// Public Endpoints (for login page / subdomain routing)
// ============================================

/**
 * Get branding by subdomain (public, no authentication required).
 * Used for branded login pages when accessing via {practice}.healiospace.com
 *
 * @param subdomain - The practice subdomain (e.g., "smithpractice")
 */
export const getBrandingBySubdomain = async (
  subdomain: string
): Promise<BrandingPreviewDto | null> => {
  try {
    const response = await publicBrandingApi.get<BrandingPreviewDto>(
      `/by-subdomain/${encodeURIComponent(subdomain)}`
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      // No branding found for this subdomain - not an error, use defaults
      return null;
    }
    console.error('Error getting branding by subdomain:', error);

    return null;
  }
};

// ============================================
// Caching Utilities
// ============================================

const BRANDING_CACHE_KEY = 'healio_practice_branding';
const BRANDING_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CachedBranding {
  branding: BrandingPreviewDto;
  timestamp: number;
}

/**
 * HA-276: transitional cleanup. Wipes the pre-HA-267 localStorage
 * cache entry on logout so users who still carry a stale legacy cache
 * (from before the HA-267 swap to /branding/me) get a clean state.
 * Called by BrandingContext.clearBranding alongside
 * {@link clearCachedMyBranding}. Eventually drop once telemetry shows
 * the legacy key is gone everywhere.
 */
export const clearCachedBranding = (): void => {
  try {
    localStorage.removeItem(BRANDING_CACHE_KEY);
  } catch (error) {
    console.error('Error clearing cached branding:', error);
  }
};

// HA-267: separate cache key for /me so a stale legacy /preview cache
// can't suppress the new endpoint's first authoritative read.
const MY_BRANDING_CACHE_KEY = 'healio:branding:me';

// HA-447: a support view-as tab neither reads nor writes these caches. localStorage is shared with
// the admin's own tab and outlives the session.
const getCachedMyBranding = (): BrandingPreviewDto | null => {
  if (isViewAs()) return null;
  try {
    const cached = localStorage.getItem(MY_BRANDING_CACHE_KEY);

    if (!cached) return null;
    const parsed: CachedBranding = JSON.parse(cached);

    if (Date.now() - parsed.timestamp > BRANDING_CACHE_TTL_MS) {
      localStorage.removeItem(MY_BRANDING_CACHE_KEY);

      return null;
    }

    return parsed.branding;
  } catch (error) {
    console.error('Error reading cached my-branding:', error);
    localStorage.removeItem(MY_BRANDING_CACHE_KEY);

    return null;
  }
};

const setCachedMyBranding = (branding: BrandingPreviewDto): void => {
  if (isViewAs()) return;
  try {
    localStorage.setItem(
      MY_BRANDING_CACHE_KEY,
      JSON.stringify({ branding, timestamp: Date.now() })
    );
  } catch (error) {
    console.error('Error caching my-branding:', error);
  }
};

export const clearCachedMyBranding = (): void => {
  try {
    localStorage.removeItem(MY_BRANDING_CACHE_KEY);
  } catch (error) {
    console.error('Error clearing cached my-branding:', error);
  }
};

/**
 * HA-267: Cached fetch for /api/v1/branding/me. Mirrors
 * {@link getBrandingWithCache} but on the new JWT-scoped endpoint.
 */
export const getMyBrandingWithCache = async (
  forceRefresh = false
): Promise<BrandingPreviewDto | null> => {
  if (!forceRefresh) {
    const cached = getCachedMyBranding();

    if (cached) {
      return cached;
    }
  }

  const branding = await getMyBranding();

  if (branding) {
    setCachedMyBranding(branding);
  }

  return branding;
};
